function assertValidId(item) {
    if (typeof item.id !== "string" || item.id.trim().length === 0) {
        throw new TypeError("Every feed item must have a non-empty string id.");
    }
}
function uniqueItems(items, excludedIds = new Set()) {
    const seen = new Set(excludedIds);
    const unique = [];
    for (const item of items) {
        assertValidId(item);
        if (seen.has(item.id)) {
            continue;
        }
        seen.add(item.id);
        unique.push(item);
    }
    return unique;
}
function normalizedTotal(reportedTotal, materializedCount) {
    if (reportedTotal === undefined) {
        return null;
    }
    if (!Number.isInteger(reportedTotal) || reportedTotal < 0) {
        throw new TypeError("A feed total must be a non-negative integer.");
    }
    return Math.max(reportedTotal, materializedCount);
}
export function createFeedState(firstPage) {
    const items = uniqueItems(firstPage.items);
    return {
        items,
        pendingNewerItems: [],
        nextOlderCursor: firstPage.nextOlderCursor,
        hasOlder: firstPage.nextOlderCursor !== null,
        totalAvailable: normalizedTotal(firstPage.totalAvailable, items.length),
        pagesLoaded: 1,
    };
}
/**
 * Adds automatically delivered items immediately at the top. The incoming
 * batch must itself be newest-to-oldest. Existing IDs are ignored.
 */
export function prependNewerItems(state, newerItems) {
    const existingIds = new Set([
        ...state.items.map(({ id }) => id),
        ...state.pendingNewerItems.map(({ id }) => id),
    ]);
    const uniqueNewer = uniqueItems(newerItems, existingIds);
    if (uniqueNewer.length === 0) {
        return state;
    }
    return {
        ...state,
        items: [...uniqueNewer, ...state.pendingNewerItems, ...state.items],
        pendingNewerItems: [],
        totalAvailable: state.totalAvailable === null
            ? null
            : state.totalAvailable + uniqueNewer.length,
    };
}
/**
 * Holds newer items while the reader is away from the top. Calling this more
 * than once preserves newest-to-oldest delivery order without duplicates.
 */
export function stageNewerItems(state, newerItems) {
    const existingIds = new Set([
        ...state.items.map(({ id }) => id),
        ...state.pendingNewerItems.map(({ id }) => id),
    ]);
    const uniqueNewer = uniqueItems(newerItems, existingIds);
    if (uniqueNewer.length === 0) {
        return state;
    }
    return {
        ...state,
        pendingNewerItems: [...uniqueNewer, ...state.pendingNewerItems],
        totalAvailable: state.totalAvailable === null
            ? null
            : state.totalAvailable + uniqueNewer.length,
    };
}
export function integrateStagedNewerItems(state) {
    if (state.pendingNewerItems.length === 0) {
        return state;
    }
    return {
        ...state,
        items: [...state.pendingNewerItems, ...state.items],
        pendingNewerItems: [],
    };
}
/**
 * Appends a finite page of older history. Page overlap is expected and is
 * de-duplicated by identity; there is no fixed maximum number of loaded items.
 */
export function appendOlderPage(state, olderPage) {
    const existingIds = new Set([
        ...state.items.map(({ id }) => id),
        ...state.pendingNewerItems.map(({ id }) => id),
    ]);
    const uniqueOlder = uniqueItems(olderPage.items, existingIds);
    const materializedCount = state.items.length + state.pendingNewerItems.length + uniqueOlder.length;
    const reportedTotal = olderPage.totalAvailable === undefined
        ? state.totalAvailable ?? undefined
        : olderPage.totalAvailable;
    return {
        ...state,
        items: [...state.items, ...uniqueOlder],
        nextOlderCursor: olderPage.nextOlderCursor,
        hasOlder: olderPage.nextOlderCursor !== null,
        totalAvailable: normalizedTotal(reportedTotal, materializedCount),
        pagesLoaded: state.pagesLoaded + 1,
    };
}
export function pendingNewerCount(state) {
    return state.pendingNewerItems.length;
}
export function materializedItemCount(state) {
    return state.items.length + state.pendingNewerItems.length;
}
export function isNearFeedEnd(metrics, preloadDistance = 320) {
    return (metrics.scrollHeight - metrics.scrollTop - metrics.clientHeight <
        preloadDistance);
}
